<template>
    <figure :class="chartContainerClass">
        <div :class="chartContainerClassContent">
            <div class="chart--content" ref="chart"/>
        </div>
        <figcaption>
            <chart-legend :school-legend="legendItems" :type="type"/>
        </figcaption>
    </figure>
</template>

<script>
    import * as d3 from 'd3';
    import chartMixin from '../chart-mixin.js';
    import { MONOCHROME_DARK_GRAY, STY_GREY_7, MONOCHROME_BLACK, THEMES_BRAND, THEMES_BRAND_LIGHT } from '../constants';
    import ChartLegend from '../chart-legend/chart-legend.vue';

    export default {
        components: {
            ChartLegend,
        },
        mixins: [chartMixin],
        props: {
            type: {
                type: String,
                default: '',
            },
            data: {
                type: [String, Array],
                default: () => [],
            },
        },
        data() {
            return {
                legendItems: [],
            };
        },
        methods: {
            /* eslint-disable max-lines-per-function */
            renderVerticalStackedBarChart() {
                const width = 720;
                const height = 250;
                const padding = 40;

                // const container = d3.select(this.$refs.legend);
                const svg = this.setupSVG(width, height);

                const categories = Array.from(new Set(this.chartData.map(d => d.name)));
                const groupedData = d3.group(this.chartData, d => d.name);

                const summedData = d3.rollups(
                    this.chartData,
                    v => d3.sum(v, d => d.value),
                    d => d.name,
                );

                const maxSum = d3.max(summedData, d => d[1]);
                const gridLines = [333.3, 666.6, 1000, 1333.3, 1666.6];

                const stackData = categories.map(name => {
                    let yOffset = 0;
                    return groupedData.get(name).map(segment => {
                        let newSegment = {
                            ...segment,
                            y0: yOffset,
                            y1: yOffset + segment.value,
                        };
                        yOffset += segment.value;
                        return newSegment;
                    });
                });

                const xScale = d3.scaleBand()
                    .domain(categories)
                    .range([padding, width - padding])
                    .padding(0.2);

                const xAxis = d3.axisBottom(xScale).tickSize(0);
                svg.append('g')
                    .attr('class', 'chart--bar-vertical-stacked-x-axis')
                    .attr('transform', `translate(0, ${ height - padding })`)
                    .call(xAxis)
                    .selectAll('text')
                    .attr('class', 'chart--bar-vertical-stacked-x-axis-text')
                    .attr('transform', 'translate(0,8)')
                    .attr('color', MONOCHROME_DARK_GRAY)
                    .attr('font-size', '12px');

                const yScale = d3.scaleLinear()
                    .domain([0, maxSum])
                    .range([height - padding, padding * 2]);

                svg.selectAll('.grid-line')
                    .data(gridLines)
                    .enter()
                    .append('line')
                    .attr('class', 'grid-line')
                    .attr('x1', padding)
                    .attr('x2', width - padding)
                    .attr('y1', d => yScale(d))
                    .attr('y2', d => yScale(d))
                    .attr('stroke', STY_GREY_7)
                    .attr('stroke-width', 1);

                svg.append('text')
                    .attr('transform', 'rotate(-90)')
                    .attr('y', 0)
                    .attr('x', 0 - (height / 2))
                    .attr('dy', '1em')
                    .style('text-anchor', 'middle')
                    .text('Kandidat*innen')
                    .attr('fill', MONOCHROME_DARK_GRAY)
                    .attr('font-size', '12px');

                svg.selectAll('.stack')
                    .data(stackData)
                    .enter()
                    .append('g')
                    .attr('transform', d => `translate(${ xScale(d[0].name) }, 0)`)
                    .selectAll('rect')
                    .data(d => d)
                    .enter()
                    .append('rect')
                    .attr('x', 0)
                    .attr('y', d => yScale(d.y1))
                    .attr('width', xScale.bandwidth())
                    .attr('height', d => yScale(d.y0) - yScale(d.y1))
                    .attr('fill', d => d.color);

                const textYPosition = 40;

                categories.forEach(category => {
                    const segments = this.chartData.filter(d => d.name === category);
                    const values = segments.map(d => d.value);
                    const colors = segments.map(d => d.color);
                    const dotRadius = 5;
                    const spacing = 0;
                    let currentXOffset = xScale.bandwidth() / 5;

                    values.forEach((value, idx) => {
                        const xPos = xScale(category) + currentXOffset;

                        svg.append('circle')
                            .attr('cx', xPos - dotRadius - 2)
                            .attr('cy', textYPosition - dotRadius)
                            .attr('r', dotRadius)
                            .attr('fill', colors[idx]);

                        svg.append('text')
                            .attr('x', xPos + dotRadius + spacing)
                            .attr('y', textYPosition)
                            .attr('text-anchor', 'start')
                            .attr('font-size', '12px')
                            .attr('fill', MONOCHROME_BLACK)
                            .text(value);

                        const textSize = value.toString().length * 15;
                        const totalPairWidth = 2 * dotRadius + spacing + textSize;
                        currentXOffset += totalPairWidth + spacing;
                    });
                });

                svg.select('.chart--bar-vertical-stacked-x-axis')
                    .selectAll('path')
                    .style('stroke', STY_GREY_7);

                svg.select('.chart--bar-vertical-stacked-x-axis')
                    .selectAll('line')
                    .style('stroke', STY_GREY_7);
            },
            generateLegendItems() {
                const colorLegends = [
                    { color: THEMES_BRAND, text: 'Bestanden' },
                    { color: THEMES_BRAND_LIGHT, text: 'Nicht bestanden' },
                ];
                colorLegends.forEach(legend => {
                    this.legendItems.push({ legendColor: legend.color, legendText: legend.text });
                });

                const uniqueSchools = Array.from(new Set(this.chartData.map(d => d.name)));
                uniqueSchools.forEach(school => {
                    const schoolData = this.chartData.find(d => d.name === school && d.description);
                    if (schoolData) {
                        const [beforeEquals, afterEquals] = schoolData.description.split('=');
                        this.legendItems.push({ name: beforeEquals.trim(), value: afterEquals.trim() });
                    }
                });
            },
        },
        mounted() {
            this.renderVerticalStackedBarChart();
            this.generateLegendItems();
        },
    };
</script>
